import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Card, Form, Modal, Spinner, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { useAuth } from '../auth';
import {
  getPricing, activateFree, createSubscriptionOrder, changePlan,
  requestActivation, verifyGatewayPayment, featureValueLabel, money
} from '../plansApi';

// Shared plan grid + purchase flow (membership_approval_flow.md). Used by the public Pricing page
// and by SubscriptionGate (the hard gate an expired member sees).
//
// A paid selection creates a server-priced order, then:
//   - an online gateway is configured  -> "Pay online" activates automatically once confirmed
//   - otherwise (the default)           -> the member submits a cash / bank-transfer payment claim
//                                          which the website admin verifies and approves.
// The member never self-activates a paid plan.

const PAYMENT_METHODS = [
  ['BANK_TRANSFER', 'Bank transfer / NEFT / IMPS / RTGS'],
  ['UPI', 'UPI'],
  ['CASH', 'Cash'],
  ['CHEQUE', 'Cheque'],
  ['OTHER', 'Other']
];

export default function PlanChooser({ data: dataProp, onChanged, showComparison = false }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(dataProp || null);
  const [cycle, setCycle] = useState('MONTHLY');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState('');
  const [payFor, setPayFor] = useState(null); // { order, plan }
  const [form, setForm] = useState({ paymentMethod: 'BANK_TRANSFER', claimedReference: '', claimedPaidAt: '', ownerNote: '' });

  const isOwner = !!user && (user.role === 'owner' || (user.roles || []).includes('owner'));

  const load = () => getPricing()
    .then((d) => { setData(d); if (onChanged) onChanged(d); })
    .catch(() => setError('Unable to load plans right now.'));

  useEffect(() => { if (!dataProp) load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (dataProp) setData(dataProp); }, [dataProp]);

  const features = useMemo(() => {
    if (!data) return [];
    const used = new Set();
    data.plans.forEach((p) => p.features.forEach((f) => used.add(f.featureCode)));
    return data.featureCatalogue.filter((f) => used.has(f.featureCode));
  }, [data]);

  if (error) return <Alert variant="danger">{error}</Alert>;
  if (!data) return <div className="text-center py-4"><Spinner animation="border" /></div>;
  if (!data.moduleEnabled) {
    return <Alert variant="info">Subscription plans are not enabled yet. All features are available to every business.</Alert>;
  }

  const priceFor = (plan) => (cycle === 'YEARLY' ? plan.yearlyPrice : plan.monthlyPrice);
  const valueInPlan = (plan, code) => {
    const entry = plan.features.find((f) => f.featureCode === code);
    return entry ? entry.value : false;
  };

  const handleFree = async (plan) => {
    setBusy(plan.code); setError(''); setNotice('');
    try {
      await activateFree(plan.code);
      setNotice(`Your free ${plan.name} plan is active. Enjoy!`);
      load();
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to activate the free plan.');
    } finally { setBusy(''); }
  };

  const handleChoosePaid = async (plan) => {
    if (!user) { navigate('/signup'); return; }
    setBusy(plan.code); setError(''); setNotice('');
    try {
      let order;
      if (data.owner?.currentPlanCode && data.owner.currentStatus && data.owner.currentPlanCode !== plan.code) {
        const change = await changePlan(plan.code, cycle);
        if (change.kind === 'downgrade') {
          setNotice(`Your plan will change to ${plan.name} at the end of your current billing cycle.`);
          load(); setBusy(''); return;
        }
        if (change.kind === 'same') { setBusy(''); return; }
        order = change; // upgrade -> an order to pay
      } else {
        order = await createSubscriptionOrder(plan.code, cycle);
      }
      setForm({ paymentMethod: 'BANK_TRANSFER', claimedReference: '', claimedPaidAt: '', ownerNote: '' });
      setPayFor({ order, plan });
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to start that order.');
    } finally { setBusy(''); }
  };

  const payOnline = async () => {
    setBusy('pay'); setError('');
    try {
      await verifyGatewayPayment({ orderId: payFor.order.orderId, paymentReference: `GATEWAY-${Date.now()}` });
      setNotice(`Payment confirmed — your ${payFor.plan.name} plan is active.`);
      setPayFor(null); load();
    } catch (err) {
      setError(err.response?.data?.msg || 'Online payment is not available right now — pay by cash / bank transfer instead.');
    } finally { setBusy(''); }
  };

  const submitRequest = async () => {
    setBusy('pay'); setError('');
    try {
      await requestActivation({
        orderId: payFor.order.orderId,
        paymentMethod: form.paymentMethod,
        claimedReference: form.claimedReference.trim(),
        claimedPaidAt: form.claimedPaidAt || undefined,
        ownerNote: form.ownerNote || undefined
      });
      setNotice('Payment details submitted. Your membership activates once the website admin verifies the payment.');
      setPayFor(null); load();
    } catch (err) {
      setError(err.response?.data?.msg || 'Unable to submit your payment details.');
    } finally { setBusy(''); }
  };

  const ctaFor = (plan) => {
    const owner = data.owner;
    if (isOwner && owner?.currentPlanCode === plan.code && owner.currentStatus) {
      return <Button variant="outline-secondary" disabled>Current plan</Button>;
    }
    const canFree = isOwner && plan.freeActivation?.enabled && !owner?.freeActivationUsed && !owner?.currentStatus;
    if (canFree) {
      const months = Math.round(plan.freeActivation.durationDays / 30);
      return (
        <Button variant={plan.recommended ? 'primary' : 'outline-primary'} disabled={busy === plan.code} onClick={() => handleFree(plan)}>
          {busy === plan.code ? '…' : `Activate FREE for ${months} month${months === 1 ? '' : 's'}`}
        </Button>
      );
    }
    const label = !user ? 'Get started'
      : (owner?.currentStatus ? 'Switch to this plan' : `Request ${plan.name}`);
    return (
      <Button variant={plan.recommended ? 'primary' : 'outline-primary'} disabled={busy === plan.code} onClick={() => handleChoosePaid(plan)}>
        {busy === plan.code ? '…' : label}
      </Button>
    );
  };

  const gateway = payFor?.order?.paymentMode === 'gateway';

  return (
    <div>
      <div className="text-center mb-3">
        <ToggleButtonGroup type="radio" name="cycle" value={cycle} onChange={setCycle}>
          <ToggleButton id="pc-cycle-m" value="MONTHLY" variant="outline-secondary">Monthly</ToggleButton>
          <ToggleButton id="pc-cycle-y" value="YEARLY" variant="outline-secondary">Yearly</ToggleButton>
        </ToggleButtonGroup>
      </div>

      {notice && <Alert variant="success" onClose={() => setNotice('')} dismissible>{notice}</Alert>}
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <div className="row g-3 justify-content-center">
        {data.plans.map((plan) => (
          <div className="col-md-4" key={plan.code}>
            <Card className={`h-100 ${plan.recommended ? 'border-primary shadow-sm' : ''}`}>
              {plan.recommended && (
                <div className="text-center bg-primary text-white small py-1 fw-semibold">MOST POPULAR</div>
              )}
              <Card.Body className="d-flex flex-column">
                <Card.Title>{plan.name}</Card.Title>
                <p className="text-muted small">{plan.tagline}</p>
                <div className="my-2">
                  <span className="fs-3 fw-bold">{money(priceFor(plan), plan.currency)}</span>
                  <span className="text-muted">/{cycle === 'YEARLY' ? 'year' : 'month'}</span>
                </div>
                {cycle === 'YEARLY' && plan.annualSavingPct > 0 && (
                  <Badge bg="success" className="align-self-start mb-2">Save {plan.annualSavingPct}% vs monthly</Badge>
                )}
                {plan.freeActivation?.enabled && (
                  <div className="small text-success mb-2">
                    Launch offer: activate free for {Math.round(plan.freeActivation.durationDays / 30)} months
                  </div>
                )}
                <ul className="small ps-3 flex-grow-1">
                  {plan.features.filter((f) => f.valueType === 'BOOLEAN' && (f.value === true))
                    .slice(0, 6).map((f) => <li key={f.featureCode}>{f.name}</li>)}
                  {plan.features.filter((f) => f.valueType === 'QUANTITY').map((f) => (
                    <li key={f.featureCode}>{featureValueLabel(f.value)} {f.name.toLowerCase()}</li>
                  ))}
                </ul>
                <div className="d-grid mt-2">{ctaFor(plan)}</div>
              </Card.Body>
            </Card>
          </div>
        ))}
      </div>

      {showComparison && (
        <>
          <h5 className="mt-5">Compare all features</h5>
          <div className="table-responsive">
            <table className="table table-bordered table-sm align-middle">
              <thead>
                <tr>
                  <th>Feature</th>
                  {data.plans.map((p) => <th key={p.code} className="text-center">{p.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {features.map((f) => (
                  <tr key={f.featureCode}>
                    <td>{f.name}{f.description ? <div className="text-muted small">{f.description}</div> : null}</td>
                    {data.plans.map((p) => (
                      <td key={p.code} className="text-center">{featureValueLabel(valueInPlan(p, f.featureCode))}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <Modal show={!!payFor} onHide={() => setPayFor(null)}>
        <Modal.Header closeButton><Modal.Title>Activate {payFor?.plan.name}</Modal.Title></Modal.Header>
        <Modal.Body>
          {payFor && (
            <>
              <p>
                <strong>{payFor.plan.name}</strong> — {money(payFor.order.amount, payFor.order.currency)}
                {' '}({(payFor.order.billingCycle || 'MONTHLY').toLowerCase()})
              </p>

              {gateway && (
                <div className="d-grid mb-3">
                  <Button variant="primary" disabled={busy === 'pay'} onClick={payOnline}>
                    {busy === 'pay' ? 'Processing…' : 'Pay online now'}
                  </Button>
                </div>
              )}

              <div className="border rounded p-3">
                <div className="fw-semibold mb-2">
                  {gateway ? 'Or pay by cash / bank transfer' : 'Pay by cash / bank transfer'}
                </div>
                <Alert variant="info" className="small">
                  Pay to the platform account, then enter the details below. Your membership activates
                  once the website admin has verified the payment.
                </Alert>
                <Form.Group className="mb-2">
                  <Form.Label className="small">How did you pay?</Form.Label>
                  <Form.Select size="sm" value={form.paymentMethod}
                    onChange={(e) => setForm({ ...form, paymentMethod: e.target.value })}>
                    {PAYMENT_METHODS.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
                  </Form.Select>
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Payment reference</Form.Label>
                  <Form.Control size="sm" placeholder="UTR / UPI ref / cheque no. / receipt no."
                    value={form.claimedReference} onChange={(e) => setForm({ ...form, claimedReference: e.target.value })} />
                </Form.Group>
                <Form.Group className="mb-2">
                  <Form.Label className="small">Date paid (optional)</Form.Label>
                  <Form.Control size="sm" type="date" value={form.claimedPaidAt}
                    onChange={(e) => setForm({ ...form, claimedPaidAt: e.target.value })} />
                </Form.Group>
                <Form.Group>
                  <Form.Label className="small">Note for the admin (optional)</Form.Label>
                  <Form.Control size="sm" as="textarea" rows={2} value={form.ownerNote}
                    onChange={(e) => setForm({ ...form, ownerNote: e.target.value })} />
                </Form.Group>
              </div>
            </>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setPayFor(null)}>Cancel</Button>
          <Button variant="primary" disabled={!form.claimedReference.trim() || busy === 'pay'} onClick={submitRequest}>
            {busy === 'pay' ? 'Submitting…' : 'Submit for approval'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
